<script setup lang="ts">
import { ref } from "vue";
import IconManager from "@/core/IconManager.vue";
import CustomDialog from "@/core/components/CustomDialog.vue";
import { useI18n } from "vue-i18n";
import { ResourceResumed } from "@/modules/resources/models/resource-resumed.js";
import CustomButton from "@/core/components/CustomButton.vue";
import CustomInput from "@/core/components/CustomInput.vue";
import { ResourcePermanency } from "@/modules/resources/models/resource-permanecy.js";
import { DateRange } from "./EventDetailEditPage.vue";
import { useToast } from "@/core/composables/use-toast.js";
const props = defineProps<{
  data: ResourceResumed;
  intervalDate: DateRange[];
  submitCallBack: (
    resourceId: string,
    quantity: number,
    resourcePermanency: ResourcePermanency,
  ) => Promise<void>;
}>();

const isDialogOpen = ref(false);
const isSubmitting = ref(false);
const selectedQuantity = ref<number>(1);
const selectedCheckBox = ref<boolean>(false);
const imageFailed = ref(false);

const { t } = useI18n();
const { showError } = useToast();

async function handleSubmit() {
  isSubmitting.value = true;
  try {
    await props.submitCallBack(
      props.data.id,
      selectedQuantity.value,
      selectedCheckBox.value
        ? ResourcePermanency.IntegralTime
        : ResourcePermanency.OnlyAssembly,
    );
    isDialogOpen.value = false;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isSubmitting.value = false;
  }
}

function getImageUrl(width: number): string {
  return `${import.meta.env.VITE_API_URL}/Resources/${props.data.id}/image?w=${width}&v=${props.data.imageVersion}`;
}
</script>

<template>
  <div
    class="flex flex-row items-center justify-between bg-background px-5 py-4 shadow-sm rounded-xl gap-4"
  >
    <div class="flex flex-row items-center gap-4">
      <div class="rounded-full bg-secondary p-2">
        <img
          v-if="data.imageVersion && !imageFailed"
          :src="getImageUrl(64)"
          :alt="data.name"
          class="size-8 rounded-full object-cover"
          @error="imageFailed = true"
        />
        <IconManager v-else name="Picture" class="text-primary" />
      </div>
      <div class="flex flex-col gap-1">
        <div class="flex flex-row items-center gap-2">
          <span class="font-bold text-sm">{{ data.name }}</span>
        </div>
        <span class="text-light-gray text-xs">
          {{
            t("events.detail.avail") +
            ": " +
            data.quantity +
            " · " +
            data.cost +
            "€"
          }}
        </span>
      </div>
    </div>
    <CustomButton @click="isDialogOpen = true" :disabled="isSubmitting">
      <IconManager name="Add" />
    </CustomButton>
  </div>
  <CustomDialog
    v-model="isDialogOpen"
    :title="t('events.detail.addResource')"
    :is-submitting="isSubmitting"
    @submit="handleSubmit"
    :disable-submit-button="
      data.quantity == 0 || selectedQuantity > data.quantity
    "
    @cancel="isDialogOpen = false"
  >
    <div class="flex flex-col gap-2">
      <CustomInput :title="t('common.name')" readonly v-model="data.name" />
      <div class="flex flex-col">
        <div class="flex flex-row gap-2 items-center">
          <label>{{ t("common.quantity") }}</label>
          <label class="text-sm text-light-gray"
            >({{ t("events.detail.available") + ": " + data.quantity }})</label
          >
        </div>
        <CustomInput
          type="number"
          v-model="selectedQuantity"
          :max="data.quantity"
        />
        <div class="flex flex-row justify-center gap-2 mt-2">
          <input
            type="checkbox"
            v-model="selectedCheckBox"
            class="cursor-pointer"
          />
          <span>{{ t("events.detail.notFullPresence") }}</span>
        </div>
      </div>
    </div>
  </CustomDialog>
</template>
